/**
 * Iter-234 — "What's New" walkthrough modal.
 *
 * Server-backed walkthrough that auto-opens after login for talent + client
 * users when a new release version is available. Replaces the previous
 * localStorage-only approach so state survives device changes, logout, and
 * "Don't show again" is honored across sessions.
 *
 * UX:
 *   - Auto-opens once per user per `current_version` (from /api/whats-new/current)
 *   - Icon grid of new features (no screenshots in v1 — icons + copy)
 *   - "Got it" → POST /acknowledge (don't_show_again from checkbox state)
 *   - "Learn more" → navigates to role-specific Help page anchored to the
 *     "Booking Actions menu" article
 *   - "Don't show this again" checkbox → persists in user_whats_new_state
 *   - Reopenable from Help menu (HelpMenu calls POST /reset then triggers
 *     `ievntur:open-whats-new` so this component picks it up).
 */
import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from './ui/dialog';
import { Button } from './ui/button';
import { Checkbox } from './ui/checkbox';
import {
  Sparkles, Image as ImageIcon, ClipboardCheck, Receipt, Shirt, UserCheck,
  Info, DollarSign, CalendarPlus, LogOut, BookOpen, MessageSquare, Mic,
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';

export const WHATS_NEW_OPEN_EVENT = 'ievntur:open-whats-new';

const ICON_MAP = {
  ImageIcon, ClipboardCheck, Receipt, Shirt, UserCheck, Info,
  DollarSign, CalendarPlus, LogOut, MessageSquare, Mic, Sparkles,
};

const TONE_BG = {
  cyan: 'bg-cyan-50 text-cyan-700 border-cyan-200',
  violet: 'bg-violet-50 text-violet-700 border-violet-200',
  emerald: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  pink: 'bg-pink-50 text-pink-700 border-pink-200',
  amber: 'bg-amber-50 text-amber-700 border-amber-200',
  slate: 'bg-slate-50 text-slate-700 border-slate-200',
  blue: 'bg-blue-50 text-blue-700 border-blue-200',
  rose: 'bg-rose-50 text-rose-700 border-rose-200',
};

const WhatsNewModal = () => {
  const { user } = useAuth() || {};
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [content, setContent] = useState(null);
  const [version, setVersion] = useState(null);
  const [dontShowAgain, setDontShowAgain] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const fetchedRef = useRef(false);

  const role = user?.role;
  const eligible = role === 'talent' || role === 'client';

  // --- Coordinate with other auto-popups (e.g. ProfileBoostPromoModal) --
  // While the WhatsNew flow is in-flight (fetching or open), other
  // promotional modals defer so they don't stack on top.
  useEffect(() => {
    if (!eligible) return;
    if (typeof window === 'undefined') return;
    window.__ievnturWhatsNewActive = true;
    return () => {
      window.__ievnturWhatsNewActive = false;
      window.dispatchEvent(new CustomEvent('ievntur:whats-new-closed'));
    };
  }, [eligible]);

  // Clear the flag the moment we know we won't actually show the modal
  // (so ProfileBoost can resume on next render).
  useEffect(() => {
    if (!fetchedRef.current) return;
    if (content && !open && !submitting) {
      // Modal resolved (either dismissed or never going to open).
      if (typeof window !== 'undefined') {
        window.__ievnturWhatsNewActive = false;
        window.dispatchEvent(new CustomEvent('ievntur:whats-new-closed'));
      }
    }
  }, [content, open, submitting]);

  // --- Auto-open after login --------------------------------------------
  useEffect(() => {
    if (!eligible || fetchedRef.current) return;
    // Iter-184 — never auto-open during admin impersonation.
    if (typeof localStorage !== 'undefined' && localStorage.getItem('impersonating') === 'true') return;
    // Iter-209 — skip on client pending-approval onboarding.
    if (typeof window !== 'undefined' && window.location?.pathname?.startsWith('/client/pending')) return;

    // Iter-314 P0 — Critical bug fix. The Radix Dialog renders its
    // overlay (`fixed inset-0 z-50 bg-black/80`) as soon as `open=true`,
    // which intercepts every click on the page. On deep action pages
    // like /client/job-overview/{id} or /client/shifts/{id}, this made
    // buttons like "Invite Talent" completely unclickable — the overlay
    // ate the click before it reached the button. Auto-open is now
    // restricted to HOME / DASHBOARD routes where the modal is
    // contextually expected. Users can still open the modal manually
    // from Help → What's New everywhere.
    const HOME_PATHS = [
      '/talent/home', '/talent/dashboard',
      '/client/home', '/client/dashboard',
      '/agency/home', '/agency/dashboard',
      '/admin', '/admin/',
    ];
    const p = (typeof window !== 'undefined' ? window.location?.pathname : '') || '';
    const onHome = HOME_PATHS.some((h) => p === h || p === h.replace(/\/$/, ''));
    if (!onHome) return;

    fetchedRef.current = true;
    let cancelled = false;
    (async () => {
      try {
        // Iter-263 — Don't stack on top of the blocking GPS consent modal.
        // If the talent still needs to accept the current GPS policy, defer
        // the WhatsNew popup. The user can still open it later from Help menu.
        try {
          const consent = await api.get('/gps/consent/status');
          if (consent.data?.needs_consent) return;
        } catch (_) { /* fall through */ }

        const { data } = await api.get('/whats-new/current');
        if (cancelled || !data) return;
        setContent(data.content || null);
        setVersion(data.current_version);
        setDontShowAgain(!!data.dont_show_again);
        if (data.should_show) {
          // small delay so it doesn't clobber the first paint
          setTimeout(() => !cancelled && setOpen(true), 900);
        }
      } catch (e) {
        // Silent fail — modal stays closed.
      }
    })();
    return () => { cancelled = true; };
  }, [eligible]);

  // --- Manual re-open from Help menu ------------------------------------
  useEffect(() => {
    if (!eligible) return;
    const handler = async () => {
      try {
        // Fetch fresh content (reset just cleared dismissal flags).
        const { data } = await api.get('/whats-new/current');
        setContent(data.content || null);
        setVersion(data.current_version);
        setDontShowAgain(false);
        setOpen(true);
      } catch {
        /* ignore */
      }
    };
    window.addEventListener(WHATS_NEW_OPEN_EVENT, handler);
    return () => window.removeEventListener(WHATS_NEW_OPEN_EVENT, handler);
  }, [eligible]);

  const acknowledge = async () => {
    if (!version) { setOpen(false); return; }
    setSubmitting(true);
    try {
      await api.post('/whats-new/acknowledge', {
        version,
        dont_show_again: dontShowAgain,
      });
    } catch {
      /* still close on failure — user already saw it */
    } finally {
      setSubmitting(false);
      setOpen(false);
    }
  };

  const goToHelp = async () => {
    // Acknowledge silently so we don't double-pop the modal when they
    // come back from the Help page.
    if (version) {
      try {
        await api.post('/whats-new/acknowledge', {
          version,
          dont_show_again: dontShowAgain,
        });
      } catch { /* ignore */ }
    }
    setOpen(false);
    // Iter-235d — learn_more can be either a flat { route, anchor }
    // (talent variant) or a per-role map (legacy client variant).
    const lm = content?.learn_more;
    let route = role === 'client' ? '/client/help' : '/talent/help';
    let anchor = null;
    if (lm) {
      if (lm.route) {
        route = lm.route;
        anchor = lm.anchor || null;
      } else if (lm[role]) {
        route = lm[role].route || route;
        anchor = lm[role].anchor || null;
      }
    }
    navigate(anchor ? `${route}#${anchor}` : route);
  };

  if (!eligible || !content) return null;

  const highlights = content.highlights || [];
  const featured = highlights.filter((h) => h.featured);
  const standard = highlights.filter((h) => !h.featured);
  const benefits = content.benefits || [];

  return (
    <Dialog open={open} onOpenChange={(v) => !v && !submitting && setOpen(false)}>
      <DialogContent
        className="bg-white max-w-2xl max-h-[90vh] overflow-hidden flex flex-col z-[100]"
        data-testid="whats-new-modal"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-lg">
            <Sparkles className="w-5 h-5 text-cyan-500" />
            {content.title || "What's new"}
          </DialogTitle>
          <DialogDescription className="text-sm text-slate-600">
            {content.subtitle || 'Latest features tailored for your role.'}
          </DialogDescription>
        </DialogHeader>

        <div className="overflow-y-auto pr-1 py-1 space-y-2.5" data-testid="whats-new-highlights">
          {featured.length > 0 && (
            <div className="space-y-2" data-testid="whats-new-featured">
              {featured.map((h) => {
                const Icon = ICON_MAP[h.icon] || Sparkles;
                const tone = TONE_BG[h.tone] || TONE_BG.cyan;
                return (
                  <div
                    key={h.key}
                    className={`flex items-start gap-3 p-3.5 rounded-xl border-2 ${tone}`}
                    data-testid={`whats-new-highlight-${h.key}`}
                  >
                    <span className="shrink-0 w-10 h-10 rounded-lg grid place-items-center bg-white/60 border border-current/30">
                      <Icon className="w-5 h-5" />
                    </span>
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-bold">{h.title}</div>
                      <p className="text-xs leading-snug mt-0.5 opacity-90">{h.body}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {benefits.length > 0 && (
            <div className="rounded-xl border border-slate-200 bg-slate-50 p-3" data-testid="whats-new-benefits">
              <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500 mb-1.5">Benefits</div>
              <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-3 gap-y-1">
                {benefits.map((b, i) => (
                  <li key={i} className="text-xs text-slate-700 flex items-start gap-1.5">
                    <Sparkles className="w-3 h-3 text-cyan-500 shrink-0 mt-0.5" /> {b}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {standard.length > 0 && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
              {standard.map((h) => {
                const Icon = ICON_MAP[h.icon] || Sparkles;
                const tone = TONE_BG[h.tone] || TONE_BG.slate;
                return (
                  <div
                    key={h.key}
                    className="flex gap-3 p-3 rounded-xl border border-slate-200 bg-white hover:border-cyan-300 transition-colors"
                    data-testid={`whats-new-highlight-${h.key}`}
                  >
                    <span className={`shrink-0 w-9 h-9 rounded-lg grid place-items-center border ${tone}`}>
                      <Icon className="w-4 h-4" />
                    </span>
                    <div className="min-w-0">
                      <div className="text-sm font-semibold text-slate-900">{h.title}</div>
                      <p className="text-xs text-slate-600 leading-snug mt-0.5">{h.body}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="flex items-center gap-2 pt-2 border-t border-slate-100">
          <Checkbox
            id="whats-new-dsa"
            checked={dontShowAgain}
            onCheckedChange={(v) => setDontShowAgain(v === true)}
            data-testid="whats-new-dont-show"
          />
          <label htmlFor="whats-new-dsa" className="text-xs text-slate-700 cursor-pointer select-none">
            Don&apos;t show this again
          </label>
        </div>

        <DialogFooter className="gap-2 flex-row sm:justify-end">
          <Button
            variant="outline"
            onClick={goToHelp}
            disabled={submitting}
            data-testid="whats-new-learn-more"
            className="gap-1.5"
          >
            <BookOpen className="w-4 h-4" /> {content?.learn_more?.label || 'Learn more'}
          </Button>
          <Button
            onClick={acknowledge}
            disabled={submitting}
            className="bg-cyan-600 hover:bg-cyan-700 text-white"
            data-testid="whats-new-got-it"
          >
            {submitting ? 'Saving…' : 'Got it'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default WhatsNewModal;
